
import { useCallback } from "react";
import { Investor, Contact } from "@/types/investor";

/**
 * Hook for managing pending saves
 */
export const usePendingSaves = (
  pendingSaves: React.MutableRefObject<{investor: Investor, timestamp: number}[]>,
  saveDataToSupabaseAndLocal: (
    investors: Investor[], 
    contacts: Contact[],
    processedIds: Set<string>,
    forceSync?: boolean
  ) => Promise<boolean>
) => {
  /**
   * Process all pending saves
   */
  const processPendingSaves = useCallback(async (
    allInvestors: Investor[],
    contacts: Contact[],
    processedIds: Set<string>
  ): Promise<boolean> => {
    if (pendingSaves.current.length === 0) return true;
    
    console.log(`Processing ${pendingSaves.current.length} pending saves`);
    
    try {
      // Create a map of current investors
      const investorMap = new Map(allInvestors.map(inv => [inv.id, inv]));
      
      // Apply all pending saves to the investor map
      pendingSaves.current.forEach(({investor}) => {
        investorMap.set(investor.id, investor);
        // Also make sure to add to processedIds if the investor has been enriched
        if (investor.enrichedWith) {
          processedIds.add(investor.id);
        }
      });
      
      // Create a new array from the map values
      const updatedInvestors = Array.from(investorMap.values());
      
      // Force sync to database by passing forceSync=true
      const success = await saveDataToSupabaseAndLocal(updatedInvestors, contacts, processedIds, true);
      
      // Clear pending saves if successful
      if (success) {
        console.log(`Successfully processed and saved ${pendingSaves.current.length} pending investors`);
        pendingSaves.current = [];
      } else {
        console.error(`Failed to save ${pendingSaves.current.length} pending investors`);
      }
      
      return success;
    } catch (error) {
      console.error("Error processing pending saves:", error);
      return false;
    }
  }, [pendingSaves, saveDataToSupabaseAndLocal]);
  
  /**
   * Flush all pending saves immediately
   */
  const flushPendingSaves = useCallback(async (
    allInvestors?: Investor[],
    contacts?: Contact[],
    processedIds?: Set<string>
  ): Promise<boolean> => {
    // If we have no pending saves, return success
    if (pendingSaves.current.length === 0) return true;
    
    console.log(`Flushing ${pendingSaves.current.length} pending saves`);
    
    // If specific investors/contacts/processedIds were provided, use them
    // Otherwise process with the passed-in values from the last batch
    if (allInvestors && contacts && processedIds) {
      return processPendingSaves(allInvestors, contacts, processedIds);
    } else {
      // Return false if we don't have enough info to process the saves
      console.warn("flushPendingSaves called without required parameters");
      return false;
    }
  }, [pendingSaves, processPendingSaves]);
  
  /**
   * Save an investor update during batch processing - ALWAYS SAVES IMMEDIATELY
   */
  const saveInvestorUpdate = useCallback(async (
    investor: Investor,
    allInvestors: Investor[],
    contacts: Contact[],
    processedIds: Set<string>,
    isBatchProcessing: boolean,
    saveTimerRef: React.MutableRefObject<number | null>,
    addToPendingSaves: (investor: Investor) => number
  ): Promise<boolean> => {
    // Track investor as processed if it has been enriched
    if (investor.enrichedWith) {
      processedIds.add(investor.id);
      console.log(`Marking investor ${investor.id} as processed in processedIds set`);
    }
    
    // Always save immediately regardless of batch status
    console.log(`Saving investor ${investor.id} immediately (batch mode: ${isBatchProcessing})`);
    
    // Clear any pending save timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    // Update the investor in the array
    const updatedInvestors = allInvestors.map(inv => 
      inv.id === investor.id ? investor : inv
    );
    
    // Log the update
    console.log(`Saving investor ${investor.id} with enrichedWith=${investor.enrichedWith || 'undefined'}`);
    console.log(`Total investors in updated array: ${updatedInvestors.length}`);
    
    // Always force sync to ensure data is saved to database
    return saveDataToSupabaseAndLocal(updatedInvestors, contacts, processedIds, true);
  }, [saveDataToSupabaseAndLocal]);
  
  return {
    processPendingSaves,
    flushPendingSaves,
    saveInvestorUpdate
  };
};
